import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { MessageSquarePlus, ThumbsUp, Bug, Lightbulb, Smile, TrendingUp, Filter } from "lucide-react";

const CATEGORIES = [
  { value: "suggestion", label: "Feature Suggestion", icon: Lightbulb, color: "text-amber-500" },
  { value: "bug", label: "Bug Report", icon: Bug, color: "text-destructive" },
  { value: "ux", label: "UX Feedback", icon: Smile, color: "text-blue-500" },
];

const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

const STATUS_COLORS: Record<string, string> = {
  new: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  reviewing: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  planned: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
  in_progress: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200",
  resolved: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  closed: "bg-muted text-muted-foreground",
};

export default function FeedbackPage() {
  const { user, role } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Form state
  const [category, setCategory] = useState("suggestion");
  const [priority, setPriority] = useState("medium");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");

  const isAdmin = role === "super_admin" || role === "company_admin";

  const { data: feedbackList = [], isLoading } = useQuery({
    queryKey: ["user_feedback"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_feedback")
        .select("*")
        .order("upvotes", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: myVotes = [] } = useQuery({
    queryKey: ["feedback_votes", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("feedback_votes")
        .select("feedback_id")
        .eq("user_id", user.id);
      if (error) throw error;
      return data.map((v: any) => v.feedback_id);
    },
    enabled: !!user,
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("user_feedback").insert({
        user_id: user!.id,
        user_name: user!.email?.split("@")[0] || "User",
        category,
        priority,
        subject,
        description,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user_feedback"] });
      toast({ title: "Feedback submitted", description: "Thank you for your feedback!" });
      setOpen(false);
      setSubject("");
      setDescription("");
      setCategory("suggestion");
      setPriority("medium");
    },
    onError: () => toast({ title: "Error", description: "Failed to submit feedback", variant: "destructive" }),
  });

  const voteMutation = useMutation({
    mutationFn: async (feedbackId: string) => {
      const hasVoted = myVotes.includes(feedbackId);
      if (hasVoted) {
        const { error } = await supabase
          .from("feedback_votes")
          .delete()
          .eq("feedback_id", feedbackId)
          .eq("user_id", user!.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("feedback_votes").insert({
          feedback_id: feedbackId,
          user_id: user!.id,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user_feedback"] });
      queryClient.invalidateQueries({ queryKey: ["feedback_votes"] });
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from("user_feedback").update({ status, updated_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user_feedback"] });
      toast({ title: "Status updated" });
    },
  });

  const filtered = feedbackList.filter((f: any) => {
    if (tab !== "all" && f.category !== tab) return false;
    if (statusFilter !== "all" && f.status !== statusFilter) return false;
    if (search && !f.subject.toLowerCase().includes(search.toLowerCase()) && !f.description.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: feedbackList.length,
    suggestions: feedbackList.filter((f: any) => f.category === "suggestion").length,
    bugs: feedbackList.filter((f: any) => f.category === "bug").length,
    ux: feedbackList.filter((f: any) => f.category === "ux").length,
  };

  const topFeedback = [...feedbackList].sort((a: any, b: any) => b.upvotes - a.upvotes).slice(0, 5);

  const getCategoryIcon = (cat: string) => {
    const c = CATEGORIES.find((x) => x.value === cat);
    if (!c) return null;
    const Icon = c.icon;
    return <Icon className={`h-4 w-4 ${c.color}`} />;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Feedback Center</h1>
          <p className="text-muted-foreground">Share your ideas, report bugs, and help us improve</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button><MessageSquarePlus className="mr-2 h-4 w-4" />Submit Feedback</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Submit Feedback</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Category</label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Priority</label>
                  <Select value={priority} onValueChange={setPriority}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Subject</label>
                <Input placeholder="Brief title for your feedback" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <Textarea placeholder="Describe in detail..." value={description} onChange={(e) => setDescription(e.target.value)} rows={5} maxLength={2000} />
              </div>
              <Button className="w-full" onClick={() => submitMutation.mutate()} disabled={!subject.trim() || !description.trim() || submitMutation.isPending}>
                {submitMutation.isPending ? "Submitting..." : "Submit Feedback"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-6 text-center"><p className="text-3xl font-bold">{stats.total}</p><p className="text-sm text-muted-foreground">Total Feedback</p></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><div className="flex items-center justify-center gap-1"><Lightbulb className="h-5 w-5 text-amber-500" /><p className="text-3xl font-bold">{stats.suggestions}</p></div><p className="text-sm text-muted-foreground">Suggestions</p></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><div className="flex items-center justify-center gap-1"><Bug className="h-5 w-5 text-destructive" /><p className="text-3xl font-bold">{stats.bugs}</p></div><p className="text-sm text-muted-foreground">Bug Reports</p></CardContent></Card>
        <Card><CardContent className="pt-6 text-center"><div className="flex items-center justify-center gap-1"><Smile className="h-5 w-5 text-blue-500" /><p className="text-3xl font-bold">{stats.ux}</p></div><p className="text-sm text-muted-foreground">UX Feedback</p></CardContent></Card>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Main list */}
        <div className="lg:col-span-3 space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Input placeholder="Search feedback..." value={search} onChange={(e) => setSearch(e.target.value)} className="sm:max-w-xs" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]"><Filter className="h-4 w-4 mr-2" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="reviewing">Reviewing</SelectItem>
                <SelectItem value="planned">Planned</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="suggestion">Suggestions</TabsTrigger>
              <TabsTrigger value="bug">Bugs</TabsTrigger>
              <TabsTrigger value="ux">UX</TabsTrigger>
            </TabsList>
          </Tabs>

          {isLoading ? (
            <p className="text-muted-foreground py-8 text-center">Loading...</p>
          ) : filtered.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">No feedback found. Be the first to share!</CardContent></Card>
          ) : (
            <div className="space-y-3">
              {filtered.map((f: any) => {
                const hasVoted = myVotes.includes(f.id);
                return (
                  <Card key={f.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {/* Vote button */}
                        <button
                          onClick={() => voteMutation.mutate(f.id)}
                          className={`flex flex-col items-center justify-center min-w-[48px] rounded-lg border p-2 transition-colors ${hasVoted ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50"}`}
                        >
                          <ThumbsUp className={`h-4 w-4 ${hasVoted ? "fill-current" : ""}`} />
                          <span className="text-sm font-semibold mt-1">{f.upvotes}</span>
                        </button>
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            {getCategoryIcon(f.category)}
                            <h3 className="font-semibold truncate">{f.subject}</h3>
                            <Badge variant="outline" className={STATUS_COLORS[f.status] || ""}>{f.status.replace("_", " ")}</Badge>
                            <Badge variant="secondary" className="text-xs">{f.priority}</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2">{f.description}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span>by {f.user_name}</span>
                            <span>{new Date(f.created_at).toLocaleDateString()}</span>
                          </div>
                          {/* Admin controls */}
                          {isAdmin && (
                            <div className="flex gap-2 mt-2">
                              {["reviewing", "planned", "in_progress", "resolved", "closed"].map((s) => (
                                <Button
                                  key={s}
                                  size="sm"
                                  variant={f.status === s ? "default" : "outline"}
                                  className="text-xs h-7"
                                  onClick={() => statusMutation.mutate({ id: f.id, status: s })}
                                >
                                  {s.replace("_", " ")}
                                </Button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Sidebar - Top Feedback */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4" /> Most Requested</CardTitle>
              <CardDescription>Top voted feedback</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {topFeedback.map((f: any, i: number) => (
                <div key={f.id} className="flex items-start gap-2">
                  <span className="text-sm font-bold text-muted-foreground w-5">{i + 1}.</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{f.subject}</p>
                    <div className="flex items-center gap-2">
                      {getCategoryIcon(f.category)}
                      <span className="text-xs text-muted-foreground">{f.upvotes} votes</span>
                    </div>
                  </div>
                </div>
              ))}
              {topFeedback.length === 0 && <p className="text-sm text-muted-foreground">No feedback yet</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">How to give good feedback</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-2">
              <p>🎯 <strong>Be specific</strong> — describe what you need</p>
              <p>🐛 <strong>For bugs</strong> — include steps to reproduce</p>
              <p>💡 <strong>For ideas</strong> — explain the problem it solves</p>
              <p>👍 <strong>Vote</strong> on existing feedback to prioritize</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
